import * as THREE from 'three';
import { PRIORITY } from '../core/Engine.js';
import { Materials } from '../render/Materials.js';
import { makeRng } from '../util/math.js';
import { MapKit } from './MapKit.js';
import { MAPAS, MAPA_PADRAO, getMapa, mapaDaURL } from './maps/index.js';

/**
 * O nível. Ele não descreve mais nenhum mapa: carrega um módulo de mapa
 * (`src/world/maps/*.js`), entrega um `MapKit` para ele construir, e materializa
 * o que sobrou — malhas, colisores, luzes, spawns, navgrid e cobertura.
 *
 * A divisão é essa e é o ponto do arquivo: o MAPA diz onde as coisas ficam, o
 * KIT sabe como uma parede com vão é feita, e o LEVEL é o único que toca na
 * cena do three e na interface que os outros sistemas leem. Antes disso o mapa
 * era 1900 linhas aqui dentro e um segundo mapa significaria duplicar tudo —
 * inclusive os defeitos.
 *
 * A interface pública é contrato com IA, armas, FX, HUD e rede, que estão em
 * produção: colliders, surfaces, hitMeshes, group, playerSpawn, playerSpawnYaw,
 * enemySpawns, spawnPoints, coverPoints, bounds, localLights, navGrid,
 * findPath, isSolid, surfaceAt. Nada disso muda de nome nem de formato.
 *
 * Escolha de mapa: `?map=<id>` na URL, padrão `blackout`.
 */

/**
 * Grade de caminhabilidade + A*. Células de 0,75 m sobre a arena; cada célula
 * guarda a altura da superfície em que um agente pisaria e se a coluna do corpo
 * acima dela está livre. Tudo que a busca toca vive em arrays tipados
 * pré-alocados com carimbo de geração, então uma consulta só aloca o caminho.
 *
 * Limitação conhecida: um piso por célula. Célula sob laje reporta o chão, que
 * é a resposta certa para o pátio e para os interiores, mas significa que
 * telhado e passarela não são navegáveis — esses spawns vão como `grounded:false`.
 */
class NavField {
  constructor(bounds, colliders, cell = 0.75) {
    this.cell = cell;
    this.minX = bounds.min.x;
    this.minZ = bounds.min.z;
    this.w = Math.max(1, Math.ceil((bounds.max.x - bounds.min.x) / cell));
    this.h = Math.max(1, Math.ceil((bounds.max.z - bounds.min.z) / cell));
    const n = this.w * this.h;
    this.blocked = new Uint8Array(n);
    this.height = new Float32Array(n);
    this.g = new Float32Array(n);
    this.f = new Float32Array(n);
    this.came = new Int32Array(n);
    this.stamp = new Int32Array(n);
    this.closed = new Uint8Array(n);
    this.heap = new Int32Array(n + 1);
    this.heapN = 0;
    this.gen = 0;
    this._bake(colliders);
  }

  /** Balde de 4 m antes de assar — o jeito ingênuo é O(células x caixas). */
  _bake(colliders) {
    const BS = 4;
    const bw = Math.ceil((this.w * this.cell) / BS) + 1;
    const bh = Math.ceil((this.h * this.cell) / BS) + 1;
    const buckets = new Array(bw * bh);
    const R = 0.42;      // raio do agente
    const HEAD = 1.78;   // altura do agente
    const STAND_MAX = 2.6;
    for (let i = 0; i < colliders.length; i++) {
      const b = colliders[i];
      const x0 = Math.max(0, Math.floor((b.min.x - R - this.minX) / BS));
      const x1 = Math.min(bw - 1, Math.floor((b.max.x + R - this.minX) / BS));
      const z0 = Math.max(0, Math.floor((b.min.z - R - this.minZ) / BS));
      const z1 = Math.min(bh - 1, Math.floor((b.max.z + R - this.minZ) / BS));
      for (let z = z0; z <= z1; z++) {
        for (let x = x0; x <= x1; x++) {
          const k = z * bw + x;
          (buckets[k] || (buckets[k] = [])).push(b);
        }
      }
    }

    const half = this.cell * 0.5;
    for (let cz = 0; cz < this.h; cz++) {
      for (let cx = 0; cx < this.w; cx++) {
        const x = this.minX + cx * this.cell + half;
        const z = this.minZ + cz * this.cell + half;
        const list = buckets[Math.floor((z - this.minZ) / BS) * bw + Math.floor((x - this.minX) / BS)];
        let floor = 0;
        if (list) {
          for (let i = 0; i < list.length; i++) {
            const b = list[i];
            if (x < b.min.x - R || x > b.max.x + R || z < b.min.z - R || z > b.max.z + R) continue;
            if (b.max.y > floor && b.max.y <= STAND_MAX) floor = b.max.y;
          }
        }
        let blocked = 0;
        if (list) {
          const lo = floor + 0.18, hi = floor + HEAD;
          for (let i = 0; i < list.length; i++) {
            const b = list[i];
            if (x < b.min.x - R || x > b.max.x + R || z < b.min.z - R || z > b.max.z + R) continue;
            if (b.max.y > lo && b.min.y < hi) { blocked = 1; break; }
          }
        }
        const i = cz * this.w + cx;
        this.blocked[i] = blocked;
        this.height[i] = floor;
      }
    }
  }

  cellOf(x, z) {
    const cx = Math.floor((x - this.minX) / this.cell);
    const cz = Math.floor((z - this.minZ) / this.cell);
    if (cx < 0 || cz < 0 || cx >= this.w || cz >= this.h) return -1;
    return cz * this.w + cx;
  }

  worldX(i) { return this.minX + (i % this.w) * this.cell + this.cell * 0.5; }
  worldZ(i) { return this.minZ + Math.floor(i / this.w) * this.cell + this.cell * 0.5; }
  heightAt(x, z) { const i = this.cellOf(x, z); return i < 0 ? 0 : this.height[i]; }
  isBlocked(x, z) { const i = this.cellOf(x, z); return i < 0 ? true : !!this.blocked[i]; }

  /** Célula livre mais próxima dentro de `r` células, em espiral. Cola as pontas. */
  nearestFree(i, r = 6) {
    if (i < 0) return -1;
    if (!this.blocked[i]) return i;
    const cx = i % this.w, cz = (i / this.w) | 0;
    for (let ring = 1; ring <= r; ring++) {
      for (let dz = -ring; dz <= ring; dz++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
          const x = cx + dx, z = cz + dz;
          if (x < 0 || z < 0 || x >= this.w || z >= this.h) continue;
          const k = z * this.w + x;
          if (!this.blocked[k]) return k;
        }
      }
    }
    return -1;
  }

  _push(i) {
    let c = ++this.heapN;
    this.heap[c] = i;
    while (c > 1) {
      const p = c >> 1;
      if (this.f[this.heap[p]] <= this.f[this.heap[c]]) break;
      const t = this.heap[p]; this.heap[p] = this.heap[c]; this.heap[c] = t;
      c = p;
    }
  }

  _pop() {
    const top = this.heap[1];
    this.heap[1] = this.heap[this.heapN--];
    let c = 1;
    for (;;) {
      const l = c << 1, r = l + 1;
      let m = c;
      if (l <= this.heapN && this.f[this.heap[l]] < this.f[this.heap[m]]) m = l;
      if (r <= this.heapN && this.f[this.heap[r]] < this.f[this.heap[m]]) m = r;
      if (m === c) break;
      const t = this.heap[m]; this.heap[m] = this.heap[c]; this.heap[c] = t;
      c = m;
    }
    return top;
  }

  search(start, goal, maxNodes = 2200) {
    if (start < 0 || goal < 0) return null;
    if (start === goal) return [start];
    const gen = ++this.gen;
    this.heapN = 0;
    const gx = goal % this.w, gz = (goal / this.w) | 0;
    this.stamp[start] = gen; this.g[start] = 0; this.came[start] = -1;
    this.f[start] = 0; this.closed[start] = 0;
    this._push(start);

    let expanded = 0;
    while (this.heapN > 0 && expanded < maxNodes) {
      const cur = this._pop();
      if (this.closed[cur] === 1 && this.stamp[cur] === gen) continue;
      this.closed[cur] = 1; this.stamp[cur] = gen;
      if (cur === goal) return this._trace(goal, gen);
      expanded++;

      const cx = cur % this.w, cz = (cur / this.w) | 0;
      const cy = this.height[cur];
      for (let d = 0; d < 8; d++) {
        const dx = d < 4 ? [1, -1, 0, 0][d] : [1, 1, -1, -1][d - 4];
        const dz = d < 4 ? [0, 0, 1, -1][d] : [1, -1, 1, -1][d - 4];
        const nx = cx + dx, nz = cz + dz;
        if (nx < 0 || nz < 0 || nx >= this.w || nz >= this.h) continue;
        const ni = nz * this.w + nx;
        if (this.blocked[ni]) continue;
        if (Math.abs(this.height[ni] - cy) > 0.6) continue;
        if (d >= 4) {
          // não cortar quina passando pela diagonal entre dois sólidos
          if (this.blocked[cz * this.w + nx] || this.blocked[nz * this.w + cx]) continue;
        }
        const step = (d >= 4 ? 1.41421 : 1) * this.cell;
        const ng = this.g[cur] + step;
        const seen = this.stamp[ni] === gen;
        if (seen && this.closed[ni]) continue;
        if (!seen || ng < this.g[ni]) {
          this.stamp[ni] = gen; this.closed[ni] = 0;
          this.g[ni] = ng; this.came[ni] = cur;
          this.f[ni] = ng + Math.hypot(nx - gx, nz - gz) * this.cell * 1.02;
          this._push(ni);
        }
      }
    }
    return null;
  }

  _trace(goal, gen) {
    const out = [];
    let c = goal;
    let guard = 0;
    while (c >= 0 && guard++ < 4096) {
      out.push(c);
      if (this.stamp[c] !== gen) break;
      c = this.came[c];
    }
    out.reverse();
    return out;
  }
}

/** Lista de mapas para o menu: [{ id, nome, regiao }]. */
export { MAPAS, MAPA_PADRAO };

export class Level {
  constructor({ seed = null, map = null } = {}) {
    this.name = 'level';
    this.priority = PRIORITY.WORLD;

    this._seedOverride = seed;
    this._mapPedido = map;

    this.group = new THREE.Group();
    this.group.name = 'level';

    /** @type {THREE.Box3[]} volumes sólidos, espaço de mundo */
    this.colliders = [];
    /** @type {string[]} nome de superfície por índice de colisor, array paralelo */
    this.surfaces = [];
    /** @type {THREE.Mesh[]} tudo que uma bala pode acertar */
    this.hitMeshes = [];
    /** @type {THREE.PointLight[]} luzes locais, para os outros sistemas orçarem */
    this.localLights = [];

    this.playerSpawn = new THREE.Vector3(0, 1.68, 29.5);
    this.playerSpawnYaw = 0;

    /** Tabela de spawn etiquetada. `enemySpawns` é a visão achatada, em pés. */
    this.spawnPoints = [];
    this.enemySpawns = [];
    this.patrolPoints = [];
    this.coverPoints = [];

    this.bounds = new THREE.Box3(
      new THREE.Vector3(-34, -1, -34),
      new THREE.Vector3(34, 20, 34),
    );

    this.mapId = null;
    /** Bloco de ambiente do mapa. O Lighting lê daqui. */
    this.ambiente = {};
    /** @type {{id:string,nome:string,regiao:string}[]} para o menu */
    this.mapas = MAPAS;

    this.rng = makeRng(20260813);
    this._mats = new Map();
    this._sway = [];
    this._flicker = [];
    this._t = 0;
    this.stats = { triangles: 0, drawCalls: 0, colliders: 0 };
  }

  init(engine) {
    this.engine = engine;

    const def = getMapa(this._mapPedido || mapaDaURL());
    if (!def) {
      console.error('[level] nenhum mapa registrado — o mundo vai subir vazio');
      engine.scene.add(this.group);
      engine.provide('level', this);
      return;
    }
    this.mapa = def;
    this.mapId = def.id;
    this.ambiente = def.ambiente || {};
    this._applyBounds(def);

    const seed = this._seedOverride ?? def.seed ?? 20260813;
    this.rng = makeRng(seed);
    const kit = new MapKit({ rng: this.rng, quality: engine.quality, seed });
    this._kit = kit;
    this._mats = kit.mats;

    // O build do mapa é território de terceiros: um throw aqui não pode levar o
    // jogo junto. O que já foi emitido continua valendo e é materializado.
    try {
      def.build(kit, { rng: this.rng, seed, quality: engine.quality, engine });
    } catch (err) {
      console.error(`[level] build do mapa "${def.id}" falhou — materializando o que saiu`, err);
    }

    this.colliders = kit.colliders;
    this.surfaces = kit.surfaces;
    try { this._materialise(kit); } catch (err) { console.error('[level] materialização falhou', err); }
    this._lights(engine, def, kit);
    this._defineSpawns(def);
    this._buildNav();
    this._deriveCoverPoints();
    this.stats.colliders = this.colliders.length;
    console.log(`[level] ${def.id}: ${this.stats.drawCalls} malhas, ${this.stats.triangles} tris, ${this.colliders.length} colisores`);

    engine.scene.add(this.group);
    engine.provide('level', this);
  }

  _applyBounds(def) {
    const b = def.bounds;
    if (!b) return;
    this.bounds.min.set(b.min[0], b.min[1], b.min[2]);
    this.bounds.max.set(b.max[0], b.max[1], b.max[2]);
    if (def.player?.spawn) this.playerSpawn.set(...def.player.spawn);
    this.playerSpawnYaw = def.player?.yaw ?? 0;
  }

  // -------------------------------------------------------------------------
  // materialização — a ordem é loose, lotes, instâncias, alambrado, e ela é
  // parte do contrato: é a ordem em que as malhas entram no grupo e em
  // hitMeshes, e é o que tools/dump-level.mjs compara entre duas versões.
  // -------------------------------------------------------------------------

  _materialise(kit) {
    for (const item of kit.loose) {
      const { mesh, sway, ground } = item;
      this.group.add(mesh);
      this.hitMeshes.push(mesh);
      this.stats.drawCalls++;
      // O plano de chão não entra na contagem de triângulos: dois triângulos de
      // 180 m não dizem nada sobre custo e só poluem a comparação de versões.
      if (!ground) this.stats.triangles += this._triCount(mesh.geometry);
      if (sway) this._sway.push({ mesh, phase: sway.phase, amp: sway.amp });
    }
    kit.loose.length = 0;

    for (const [surface, batch] of kit.batches) {
      if (batch.n === 0) continue;
      const mesh = new THREE.Mesh(batch.geometry(), kit.mat(surface));
      mesh.name = `level:${surface}`;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.surface = surface === 'fabricDouble' ? 'fabric' : surface;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      this.group.add(mesh);
      this.hitMeshes.push(mesh);
      this.stats.drawCalls++;
      this.stats.triangles += batch.triangles;
    }
    kit.batches.clear();

    for (const req of kit.instances) this._instance(req.geometry, req.surface, req.rows, req.name, req.tint);
    kit.instances.length = 0;

    const fence = kit.buildFenceMesh();
    if (fence) {
      this.group.add(fence.mesh);
      this.hitMeshes.push(fence.mesh);
      this.stats.drawCalls++;
      this.stats.triangles += fence.triangles;
    }
  }

  _triCount(geo) {
    if (!geo) return 0;
    return geo.index ? geo.index.count / 3 : (geo.attributes.position?.count ?? 0) / 3;
  }

  /** Uma InstancedMesh a partir de linhas [x, y, z, yaw, escala]. */
  _instance(geo, surface, rows, name, tint = null) {
    if (!rows.length) { geo.dispose?.(); return null; }
    let mat = this._kit.mat(surface);
    if (tint) {
      const key = `${surface}:${name}`;
      mat = this._mats.get(key);
      if (!mat) {
        mat = this._kit.mat(surface).clone();
        mat.color = new THREE.Color(tint[0], tint[1], tint[2]);
        this._mats.set(key, mat);
      }
    }
    const inst = new THREE.InstancedMesh(geo, mat, rows.length);
    inst.name = `level:${name}`;
    inst.castShadow = true;
    inst.receiveShadow = true;
    inst.userData.surface = surface === 'fabricDouble' ? 'fabric' : surface;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const p = new THREE.Vector3();
    const s = new THREE.Vector3();
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      p.set(r[0], r[1], r[2]);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), r[3] || 0);
      s.setScalar(r[4] ?? 1);
      inst.setMatrixAt(i, m.compose(p, q, s));
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.computeBoundingSphere();
    this.group.add(inst);
    this.hitMeshes.push(inst);
    this.stats.drawCalls++;
    this.stats.triangles += this._triCount(geo) * rows.length;
    return inst;
  }

  // -------------------------------------------------------------------------
  // iluminação local
  // -------------------------------------------------------------------------

  _lights(engine, def, kit) {
    const ultra = engine?.quality === 'ultra';
    // [x, y, z, cor, intensidade, alcance, sombra] — do mapa, mais o que o
    // build tiver acrescentado por código via kit.addLight().
    const defs = [...(def.luzes || []), ...(kit?.lights || [])];
    for (const [x, y, z, col, inten, dist, shadow] of defs) {
      const l = new THREE.PointLight(col, inten, dist, 2);
      l.position.set(x, y, z);
      l.castShadow = !!shadow && ultra;
      if (l.castShadow) {
        l.shadow.mapSize.set(512, 512);
        l.shadow.camera.near = 0.25;
        l.shadow.camera.far = dist;
        l.shadow.bias = -0.004;
      }
      this.group.add(l);
      this.localLights.push(l);

      // luminária de verdade, para a poça de luz ter fonte visível
      const shade = new THREE.Mesh(
        new THREE.ConeGeometry(0.24, 0.2, 10, 1, true),
        this._kit.mat('gunMetal'),
      );
      shade.position.set(x, y + 0.22, z);
      shade.rotation.x = Math.PI;
      shade.userData.surface = 'gunMetal';
      this.group.add(shade);
      this.hitMeshes.push(shade);
      this.stats.drawCalls++;

      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.075, 8, 6),
        Materials.emissive(col, 6),
      );
      bulb.position.set(x, y + 0.1, z);
      this.group.add(bulb);
      this.stats.drawCalls++;
      this._flicker.push({ light: l, bulb, base: inten, phase: this.rng() * 10, rate: 0.4 + this.rng() * 1.6 });
    }
    // Só as que o mapa marcou piscam. O sorteio acima roda para TODAS mesmo
    // assim: ele faz parte do fluxo do rng, e pular consumo aqui deslocaria
    // qualquer sorteio futuro.
    const piscam = new Set(def.piscam || []);
    this._flicker = this._flicker.filter((f, i) => piscam.has(i));
  }

  // -------------------------------------------------------------------------
  // spawns, cobertura, navegação
  // -------------------------------------------------------------------------

  _defineSpawns(def) {
    const probe = new THREE.Vector3();
    let bad = 0;
    for (const [x, y, z, yaw, kind, grounded] of (def.spawns || [])) {
      probe.set(x, y + 0.95, z);
      if (this.isSolid(probe, 0.3)) {
        console.warn(`[level] spawn ${kind} @ ${x},${y},${z} está dentro da geometria — descartado`);
        bad++;
        continue;
      }
      const p = new THREE.Vector3(x, y, z);
      this.spawnPoints.push({ position: p, yaw, kind, grounded });
      if (kind !== 'patrol') this.enemySpawns.push(p.clone());
    }
    if (bad) console.warn(`[level] ${bad} spawn(s) recusado(s)`);
    // Mantém o array achatado não-vazio mesmo se a validação derrubar tudo.
    if (!this.enemySpawns.length) this.enemySpawns.push(new THREE.Vector3(0, 0, -10));
    this.patrolPoints = this.spawnPoints.filter((s) => s.kind === 'patrol').map((s) => s.position);
  }

  _buildNav() {
    try {
      this.navGrid = new NavField(this.bounds, this.colliders, 0.75);
    } catch (err) {
      console.warn('[level] assar a navgrid falhou', err);
      this.navGrid = null;
    }
  }

  /**
   * A* entre dois pontos de mundo. Devolve waypoints (Vector3, y = altura de
   * pisada) ou null quando não há rota. Células colineares são descartadas para
   * o chamador receber os cantos, não uma escadinha célula a célula.
   */
  findPath(from, to) {
    const g = this.navGrid;
    if (!g || !from || !to) return null;
    const s = g.nearestFree(g.cellOf(from.x, from.z));
    const t = g.nearestFree(g.cellOf(to.x, to.z));
    if (s < 0 || t < 0) return null;
    const cells = g.search(s, t);
    if (!cells || !cells.length) return null;
    const out = [];
    let pdx = 0, pdz = 0;
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (i > 0 && i < cells.length - 1) {
        const prev = cells[i - 1], next = cells[i + 1];
        const dx = (next % g.w) - (c % g.w), dz = ((next / g.w) | 0) - ((c / g.w) | 0);
        pdx = (c % g.w) - (prev % g.w); pdz = ((c / g.w) | 0) - ((prev / g.w) | 0);
        if (dx === pdx && dz === pdz) continue;
      }
      out.push(new THREE.Vector3(g.worldX(c), g.height[c], g.worldZ(c)));
    }
    if (out.length) out[out.length - 1].set(to.x, g.height[t], to.z);
    return out;
  }

  /**
   * Cobertura = qualquer colisor cujo topo fique entre a altura do peito e a da
   * cabeça. Quatro pontos de encosto por caixa, descartando o que estiver
   * enterrado na geometria, com deduplicação numa grade de 1,5 m para uma
   * parede longa não afogar a lista.
   */
  _deriveCoverPoints() {
    const size = new THREE.Vector3();
    const seen = new Set();
    const probe = new THREE.Vector3();
    const limX = this.bounds.max.x - 1.5, limZ = this.bounds.max.z - 1.5;
    for (let i = 0; i < this.colliders.length && this.coverPoints.length < 160; i++) {
      const box = this.colliders[i];
      box.getSize(size);
      const topY = box.max.y;
      const standY = box.min.y;
      if (topY - standY < 0.5) continue;
      if (topY < 0.75 || topY > 1.95) continue;
      if (size.x > 9 || size.z > 9) continue;
      const c = box.getCenter(new THREE.Vector3());
      const offs = [
        [size.x / 2 + 0.65, 0], [-size.x / 2 - 0.65, 0],
        [0, size.z / 2 + 0.65], [0, -size.z / 2 - 0.65],
      ];
      for (const [dx, dz] of offs) {
        const px = c.x + dx, pz = c.z + dz;
        if (Math.abs(px) > limX || Math.abs(pz) > limZ) continue;
        const key = `${Math.round(px / 1.5)}:${Math.round(pz / 1.5)}`;
        if (seen.has(key)) continue;
        probe.set(px, standY + 1.0, pz);
        if (this.isSolid(probe, 0.25)) continue;
        seen.add(key);
        this.coverPoints.push({
          position: new THREE.Vector3(px, standY, pz),
          standHeight: topY,
          normal: new THREE.Vector3(dx, 0, dz).normalize(),
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // runtime
  // -------------------------------------------------------------------------

  update(dt) {
    this._t += dt;
    const t = this._t;
    for (let i = 0; i < this._sway.length; i++) {
      const s = this._sway[i];
      // dois senos desafinados para o pano nunca cair num laço óbvio
      s.mesh.rotation.z = (Math.sin(t * 0.9 + s.phase) + Math.sin(t * 1.37 + s.phase * 1.7) * 0.5) * s.amp;
      s.mesh.rotation.x = Math.sin(t * 0.7 + s.phase * 0.5) * s.amp * 0.6;
    }
    for (let i = 0; i < this._flicker.length; i++) {
      const f = this._flicker[i];
      const n = Math.sin(t * 11.3 + f.phase) * Math.sin(t * 4.1 + f.phase * 2.3);
      const k = 0.86 + 0.14 * n - (n > 0.86 ? 0.35 : 0);
      f.light.intensity = f.base * k;
      if (f.bulb) f.bulb.material.emissiveIntensity = 6 * k;
    }
  }

  /** Nome da superfície de um acerto — usado por FX de impacto e som de passo. */
  surfaceAt(object, instanceId) {
    if (object?.userData?.surface) return object.userData.surface;
    return 'concrete';
  }

  /** Teste barato de ponto dentro de sólido, para validar spawn e granada parada. */
  isSolid(point, pad = 0) {
    for (let i = 0; i < this.colliders.length; i++) {
      const b = this.colliders[i];
      if (point.x > b.min.x - pad && point.x < b.max.x + pad &&
          point.y > b.min.y - pad && point.y < b.max.y + pad &&
          point.z > b.min.z - pad && point.z < b.max.z + pad) return true;
    }
    return false;
  }

  dispose() {
    this.group.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
    });
    for (const m of this._mats.values()) m.dispose?.();
    this._mats.clear();
  }
}
